import {
  type AutovioCalendarEvent,
  isPracticalExam,
  isTheoryExam,
  type PracticalExam,
  type TheoryExam,
} from "../model/autovioCalendarEvents";
import { DateTime } from "luxon";

export type Exam = TheoryExam | PracticalExam;
export type ExamTypeFilter = "TheoryExam" | "PracticalExam";
export type InstructorBranchIdsByInstructorId = Readonly<Record<string, ReadonlyArray<string>>>;
export type NamesById = Readonly<Record<string, string>>;

export function getRecentExamPeriodChoices(now: DateTime): Array<{ id: string; name: string }> {
  const nowInBerlin = now.setZone("Europe/Berlin");
  const monthChoices = Array.from({ length: 12 }, (_, index) => {
    const month = nowInBerlin.startOf("month").minus({ months: index }).setLocale("de");
    return { id: month.toFormat("yyyy-MM"), name: month.toFormat("LLLL yyyy") };
  });
  return [{ id: "currentWeek", name: "Aktuelle Woche" }, { id: "lastWeek", name: "Letzte Woche" }, ...monthChoices];
}

export function isExamPeriodFilter(value: unknown): value is string {
  return (
    value === "currentWeek" ||
    value === "lastWeek" ||
    (typeof value === "string" && /^\d{4}-(0[1-9]|1[0-2])$/.test(value))
  );
}

export function examMatchesPeriod(event: AutovioCalendarEvent, examPeriod: string, now: DateTime): boolean {
  const currentWeekStart = now.setZone("Europe/Berlin").startOf("week");
  const eventStart = event.start.setZone("Europe/Berlin");
  if (examPeriod === "currentWeek") {
    return eventStart >= currentWeekStart && eventStart < currentWeekStart.plus({ weeks: 1 });
  }
  if (examPeriod === "lastWeek") {
    return eventStart >= currentWeekStart.minus({ weeks: 1 }) && eventStart < currentWeekStart;
  }
  return eventStart.toFormat("yyyy-MM") === examPeriod;
}

export function examMatchesSearch(
  event: AutovioCalendarEvent,
  searchTerm: string,
  studentNamesById: NamesById,
  instructorNamesById: NamesById,
): event is Exam {
  if (!isTheoryExam(event) && !isPracticalExam(event)) {
    return false;
  }
  const searchableText = [
    studentNamesById[event.student.uid],
    instructorNamesById[event.instructorId ?? ""],
    ...Object.values(event.startLocation).filter((value): value is string => typeof value === "string"),
  ]
    .filter(Boolean)
    .join(" ")
    .toLocaleLowerCase("de");
  return searchableText.includes(searchTerm.trim().toLocaleLowerCase("de"));
}

export function isExamOnlyTypeFilter(type: unknown): boolean {
  if (type === "TheoryExam" || type === "PracticalExam") {
    return true;
  }
  return (
    Array.isArray(type) && type.length > 0 && type.every((value) => value === "TheoryExam" || value === "PracticalExam")
  );
}

export function examMatchesType(event: AutovioCalendarEvent, examType: ExamTypeFilter): event is Exam {
  return examType === "TheoryExam" ? isTheoryExam(event) : isPracticalExam(event);
}

export function examMatchesBranch(
  event: AutovioCalendarEvent,
  branchId: string,
  instructorBranchIdsByInstructorId: InstructorBranchIdsByInstructorId,
): event is Exam {
  if (isTheoryExam(event)) {
    return event.branchId === branchId;
  }
  if (isPracticalExam(event)) {
    return !!event.instructorId && !!instructorBranchIdsByInstructorId[event.instructorId]?.includes(branchId);
  }
  return false;
}
